import { useAppSelector } from "@/services/store";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import { Box, Button, Typography } from "@mui/material";
import { useLayoutEffect, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { useNavigate } from "react-router-dom";
import { ErrorFallbackArguments } from "./types";
import { parseError } from "./utils";

export function ErrorFallback({ error: { error, refetch } }: ErrorFallbackArguments): React.ReactElement {
  const { t } = useTranslation();

  const [path, setPath] = useState("");
  const { resetBoundary } = useErrorBoundary();
  const groups = useAppSelector(state => state.auth.groups) ?? [];
  const isSuperAdmin = groups.findIndex(group => group === "super_admins") > -1;
  // NOTE: if there is an issue with typescript, ErrorFallback can be called with error
  // being passed as undefined. This is a workaround to prevent the page from crashing
  const { msg, refetchable } = error
    ? parseError(error, t, isSuperAdmin)
    : { msg: t("errors.unknown"), refetchable: false };

  const location = useLocation();
  const navigate = useNavigate();

  useLayoutEffect(() => {
    if (path !== "") {
      resetBoundary();
    }
    setPath(location.key);
  }, [location]);

  const reset = async () => {
    try {
      // Use of await here allows to resolve even if refetch doesn't return a promise
      await (refetch?.() || Promise.resolve());
      resetBoundary();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Box
      role="alert"
      sx={{
        display: "flex",
        justifyContent: "center",
        width: "100%",
        textAlign: "center",
        flexWrap: "nowrap",
        alignItems: "center",
        flexDirection: "column",
      }}
    >
      <ErrorOutlineOutlinedIcon sx={{ fontSize: "40px", width: "200px", height: "200px" }} />
      <Typography sx={{ whiteSpace: "pre-line" }}>{msg}</Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "20px",
          marginTop: "20px",
        }}
      >
        {refetchable && (
          <Button variant="outlined" onClick={reset} startIcon={<ReplayOutlinedIcon />}>
            {t("errors.Try_again")}
          </Button>
        )}
        <Button variant="outlined" onClick={() => (window.location.href = "/")}>
          {t("errors.Take_me_home")}
        </Button>
      </Box>
    </Box>
  );
}
